import React, { lazy, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import InstallationDetailsTable from "./InstallationDetailsTable";
import { Accordion, AccordionTab } from "primereact/accordion";
import styled from "styled-components";
import { Spacer } from "../../../components/core/utility";
import backArrowIcon from "../../../assets/icons/back-arrow-icon.png";
import { redirectToPrevPage } from "../../C4S Decommission - Replacement/utils";
import "../ViewVehicleDetailsComponent.css";
import "./installationDetails.css";
import { PrivilegeContext } from "../../../../context/privilegeContext";
import moment from "moment";
import { getReviewDetails } from "../../../services/scheduler/getDropDownData";
import { Loader } from "@vds/loaders";
import NotificationBanner from "../../../components/NotificationBanner/NotificationBanner";

export const AccordionWrapper = styled.div`
  padding-top: 2.5rem;
  .p-accordion .p-accordion-tab .p-accordion-header .p-accordion-header-link,
  .p-accordion .p-accordion-tab .p-accordion-header:hover .p-accordion-header-link,
  .p-accordion .p-accordion-tab .p-accordion-header .p-accordion-header-link {
    color: #000000;
    border: none;
    padding: 0.5rem;
    background: #f8f7f5; //#f6f6f6
    box-shadow: unset;
    border-radius: unset;
  }

  .p-accordion .p-accordion-content {
    padding: 0rem;
    border: none;
  }
  .p-accordion-header-text {
    font-weight: 700;
    font-family: Verizon-NHG-eDS;
    flex: 1;
  }

  .p-accordion .p-accordion-tab .p-accordion-header .p-accordion-header-link {
    flex-direction: row;
  }

  .p-accordion .p-accordion-header-link .p-accordion-toggle-icon {
    order: 1;
    margin-left: auto;
    margin-right: 0;
  }
`;

const InstallationDetails = ({ summaryData }) => {
  const pageWrapperRef = useRef(null);
  const [pageWrapperHeight, setPageWrapperHeight] = useState(null);

  useEffect(() => {
    const updatePageWrapperHeight = () => {
      if (!pageWrapperRef.current) return;

      const rect = pageWrapperRef.current.getBoundingClientRect();
      const viewportHeight = window.visualViewport?.height || window.innerHeight;

      // small bottom buffer so border/shadow does not clip
      const availableHeight = Math.max(viewportHeight - rect.top - 16, 240);

      setPageWrapperHeight(availableHeight);
    };

    updatePageWrapperHeight();

    window.addEventListener("resize", updatePageWrapperHeight);
    window.visualViewport?.addEventListener("resize", updatePageWrapperHeight);

    return () => {
      window.removeEventListener("resize", updatePageWrapperHeight);
      window.visualViewport?.removeEventListener("resize", updatePageWrapperHeight);
    };
  }, []);

  const { jobId, calculatedDuration } = summaryData || {};
  const { cspInfo } = useContext(PrivilegeContext);

  //  Parse calculatedDuration into parts
  const parseDuration = (duration) => {
    if (!duration) return { hours: null, minutes: null };
    const hrMatch = duration.match(/(\d+)\s*hr/);
    const minMatch = duration.match(/(\d+)\s*min/);
    return {
      hours: hrMatch ? hrMatch[1] : null,
      minutes: minMatch ? minMatch[1] : null,
    };
  };

  const { hours, minutes } = parseDuration(calculatedDuration);

  const [isLoading, setIsLoading] = useState(true); // New state to track loading status
  const [jobSummary, setJobSummary] = useState({});
  const [summaryApiError, setSummaryApiError] = useState("");
  const [banner, setBanner] = useState(null);

  useEffect(() => {
    fetchReviewDetails();
  }, []);

  const { vehicleDurations, assetDurations } = useMemo(() => {
    const vehicleDurations = jobSummary?.vehicles?.reduce((total, vehicle) => total + vehicle.estimatedInstallDuration, 0);
    const assetDurations = jobSummary?.assets?.reduce((total, asset) => total + asset.estimatedInstallDuration, 0);
    return { vehicleDurations, assetDurations };
  }, [jobSummary]);

  const showJobBanner = useCallback((type, title, message) => {
    setBanner({ type, title, message });
  }, []);

  const fetchReviewDetails = async () => {
    try {
      setIsLoading(true);
      const response = await getReviewDetails(jobId);
      const responsedata = response?.data?.data;
      if (response.status === 200 && responsedata) {
        setJobSummary(responsedata);
        setSummaryApiError("");
      } else {
        throw new Error(response.response?.data.message || "Failed to fetch job review details");
      }
    } catch (error) {
      setSummaryApiError(error.message);
      showJobBanner("error", "Failed to fetch job review details", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    redirectToPrevPage();
  };

  return (
    <div className="installation-details-page">
      <Spacer top="10px" />

      {/* Order header */}
      <div className="id-order-header">
        <div className="id-order-number">Order #{cspInfo.data.data.CSP[0].Provider_Order}</div>
        <div className="id-opportunity-created">
          Opportunity Created: {moment(cspInfo.data.data.CSP[0].OppCreatedDateTime).format("MMMM D, YYYY")}
        </div>
      </div>

      {/* Header row */}
      <div
        className="pageWrapper "
        ref={pageWrapperRef}
        tabIndex={0}
        aria-label="Installation details content"
        style={pageWrapperHeight ? { height: `${pageWrapperHeight}px` } : undefined}
      >
        <div className="headerRow ">
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <button type="button" onClick={handleBack} className="backBtn" aria-label="Back">
              <img src={backArrowIcon} alt="" aria-hidden="true" className="backIcon" />
            </button>
            <span className="id-page-title">Installation Details</span>
          </div>
          <div className="id-duration">
            <span className="id-duration-label">Estimated total duration: &nbsp;</span>
            {hours && (
              <>
                <span className="id-duration-number">{hours}</span>
                <span className="id-duration-unit"> hr </span>
              </>
            )}
            {minutes && (
              <>
                <span className="id-duration-number" style={{ marginLeft: "2px" }}>
                  {minutes}
                </span>
                <span className="id-duration-unit"> min</span>
              </>
            )}
          </div>
        </div>
        {/* <Spacer top="10px" /> */}
        {isLoading ? (
          <Loader active={isLoading} />
        ) : (
          <>
            {summaryApiError ? (
              <div style={{ margin: "12px 0 16px 0" }}>
                <NotificationBanner type={banner.type} title={banner.title} message={banner.message} />
              </div>
            ) : (
              <div className="welcome-banner">
                {jobSummary.vehicles.length ? (
                  <AccordionWrapper>
                    <Accordion role="region" activeIndex={0} expandIcon="pi pi-chevron-down" collapseIcon="pi pi-chevron-up">
                      <AccordionTab
                        aria-label="Vehicles"
                        header={
                          <span style={{ display: "flex", alignItems: "center", width: "100%", justifyContent: "space-between" }}>
                            <span style={{ fontSize: "24px" }}>Vehicles({jobSummary.vehicles.length})</span>
                            <span style={{ fontWeight: 400, fontSize: "12px", marginRight: "4rem" }}>
                              <strong style={{ fontSize: "14px" }}>Estimated duration:</strong>
                              {Math.floor(vehicleDurations / 60) > 0 ? ` ${Math.floor(vehicleDurations / 60)} hr` : ""}
                              {vehicleDurations % 60 > 0 ? ` ${vehicleDurations % 60} min` : ""}
                            </span>
                          </span>
                        }
                      >
                        <div tabIndex={0} aria-label="vehicle list table" style={{ marginTop: ".5rem" }}>
                          <InstallationDetailsTable data={jobSummary.vehicles} isVehicle={true} />
                        </div>
                      </AccordionTab>
                    </Accordion>
                  </AccordionWrapper>
                ) : (
                  <></>
                )}
                {jobSummary.assets.length ? (
                  <AccordionWrapper>
                    <Accordion role="region" activeIndex={0} expandIcon="pi pi-chevron-down" collapseIcon="pi pi-chevron-up">
                      <AccordionTab
                        aria-label="Assets"
                        header={
                          <span style={{ display: "flex", alignItems: "center", width: "100%", justifyContent: "space-between" }}>
                            <span style={{ fontSize: "24px" }}>Powered Assets({jobSummary.assets.length})</span>
                            <span style={{ fontWeight: 400, fontSize: "12px", marginRight: "4rem" }}>
                              <strong style={{ fontSize: "14px" }}>Estimated duration:</strong>
                              {Math.floor(assetDurations / 60) > 0 ? ` ${Math.floor(assetDurations / 60)} hr` : ""}
                              {assetDurations % 60 > 0 ? ` ${assetDurations % 60} min` : ""}
                            </span>
                          </span>
                        }
                      >
                        <div tabIndex={0} aria-label="vehicle list table" style={{ marginTop: ".5rem" }}>
                          <InstallationDetailsTable data={jobSummary.assets} isVehicle={false} />
                        </div>
                      </AccordionTab>
                    </Accordion>
                  </AccordionWrapper>
                ) : (
                  <></>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default InstallationDetails;

export const InstallationDetailsMetadata = {
  name: "Installation Details",
  id: "installation_details",
  component: lazy(() => import("./InstallationDetails")),
  route: "/installation_details",
};
